import React from "react"
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query"
import {API, Auth} from "aws-amplify"
import Axios from "axios"
import {useI18next} from "gatsby-plugin-react-i18next"

import {
  cognitoSignOut,
  currentAuthenticatedUser,
  useCognitoUser,
} from "services/auth/hooks"
import {AppCenterApiName, LATEST_POLICY_VERSION} from "services/constants"
import {queryClient} from "libs/query"
import {useGiantUserStore} from "stores/user"

import {
  FamilyMemberProfile,
  FamilyMembersResponseDTO,
  GiantUserProfile,
  PhoneCodeOptions,
  RegisteredProductsResponse,
} from "./data"
import {searchCallbackUrl} from "./requests"
import {useConfirmModal} from "stores/modals"
import {ERR_USER_NOT_FOUND} from "constants/error"
import {registerCloseWindow} from "services/auth/cache"
import {
  AvailableCognitoIdentity,
  AvailableSocialProvider,
} from "services/auth/schemas"

export const QUERY_KEY_GIANT_USER = ["giantUser"]
export const QUERY_KEY_GIANT_REGISTERED_PRODUCTS = ["giantRegisteredProducts"]
const QUERY_KEY_GIANT_FAMILY_MEMBERS = ["giantFamilyMembers"]
/**
 *
 * @param {string} [gid]
 * @returns
 */
const giantFamilyMemberProfileKey = gid => ["familyMemberProfile", gid]

/**
 * @returns {Promise<import("./data").TGiantUser>}
 */
export async function getGiantUser() {
  return API.get(AppCenterApiName.AppCenterApiWithToken, "/user", {})
}

/**
 * Get detailed user address information
 * @returns {Promise<import("./data").TUserAddressResponse>}
 */
export async function getGiantUserAddress() {
  return API.get(AppCenterApiName.AppCenterApiWithToken, "/user/address", {})
}

/**
 * Get registered products for the current Giant ID account.
 * @param {object} [options]
 * @returns {Promise<import("./data").TRegisteredProductsResponse>}
 */
export async function getGiantRegisteredProducts(options = {}) {
  return API.get(
    AppCenterApiName.AppCenterApiWithoutVersionWithIdToken,
    "/actions/get-registered-products",
    options,
  ).then(data => RegisteredProductsResponse.parse(data))
}

/**
 * Store user tokens and return a code for client to retrieve tokens later
 *
 * @param {{
 *  idToken: string,
 *  accessToken: string,
 *  refreshToken: string,
 * }} tokens
 * @returns {Promise<{ code: string }>}
 */
export async function storeUserToken(tokens) {
  return API.post(AppCenterApiName.AppCenterApiWithToken, "/user-token", {
    body: {...tokens},
  })
}

/**
 * Update user address information
 * @param {object} requestBody - Request body with addresses property
 * @param {import("./data").TAddressesRequest} requestBody.addresses - Address data organized by type
 * @returns {Promise<{message: string}>}
 */
export async function updateGiantUserAddress(requestBody) {
  return API.put(AppCenterApiName.AppCenterApiWithToken, "/user/address", {
    body: requestBody,
  })
}

/**
 * @param {import("@tanstack/react-query").UseQueryOptions<
 * undefined,
 * any,
 * import("./data").TGiantUser,
 * > & {skipErrorNavigate?: boolean}} [options]
 * @returns
 */
export const useGiantUser = (options = {}) => {
  const {skipErrorNavigate, ...restOptions} = options
  const {navigate} = useI18next()
  const cognitoUser = useCognitoUser({
    skipErrorNavigate, // sometimes, we want to skip the navigate in the useCognitoUser (example, when in the sign-in page)
    refetchOnMount:
      typeof options.refetchOnMount === "boolean"
        ? options.refetchOnMount
        : undefined,
    refetchOnReconnect:
      typeof options.refetchOnReconnect === "boolean"
        ? options.refetchOnReconnect
        : undefined,
    refetchOnWindowFocus:
      typeof options.refetchOnWindowFocus === "boolean"
        ? options.refetchOnWindowFocus
        : undefined,
  })
  const setUser = useGiantUserStore(state => state.setUser)
  const giantUserQuery = useQuery({
    queryKey: QUERY_KEY_GIANT_USER,
    queryFn: ({signal}) =>
      API.get(AppCenterApiName.AppCenterApiWithToken, "/user", {
        signal,
      }),
    enabled: cognitoUser.isSuccess,
    onSuccess: async (...args) => {
      const user = await currentAuthenticatedUser()
      const [data] = args
      if (!data.policyVersions?.includes(LATEST_POLICY_VERSION)) {
        // No policy agreed, redirect to confirm sign in page
        let navigatePath = `/auth/sign-in?sub=${user.attributes.sub}`
        if (user.attributes.given_name)
          navigatePath += `&firstName=${user.attributes.given_name}`
        if (user.attributes.family_name)
          navigatePath += `&lastName=${user.attributes.family_name}`
        navigate(navigatePath)
      }
      options.onSuccess?.(...args)
    },
    onError: (...args) => {
      const data = args[0]?.response?.data
      if (data?.error?.code === ERR_USER_NOT_FOUND) {
        cognitoSignOut()
      }
      options.onError?.(...args)
    },
    ...restOptions,
  })

  React.useEffect(() => {
    if (giantUserQuery.data) {
      setUser(GiantUserProfile.parse(giantUserQuery.data))
    }
  }, [giantUserQuery.data])

  return giantUserQuery
}

export const prefetchGiantUser = () => {
  return queryClient.prefetchQuery({
    queryKey: QUERY_KEY_GIANT_USER,
    queryFn: () => getGiantUser(),
  })
}

export const QUERY_KEY_GIANT_USER_ADDRESS = ["giantUserAddress"]

/**
 * Hook to get detailed user address information
 * @param {import("@tanstack/react-query").UseQueryOptions<
 * undefined,
 * any,
 * import("./data").TUserAddressResponse,
 * > & {skipErrorNavigate?: boolean}} [options]
 * @returns
 */
export const useGiantUserAddress = (options = {}) => {
  const {skipErrorNavigate, ...restOptions} = options
  const {navigate} = useI18next()
  const cognitoUser = useCognitoUser({
    skipErrorNavigate,
    refetchOnMount:
      typeof options.refetchOnMount === "boolean"
        ? options.refetchOnMount
        : undefined,
    refetchOnReconnect:
      typeof options.refetchOnReconnect === "boolean"
        ? options.refetchOnReconnect
        : undefined,
    refetchOnWindowFocus:
      typeof options.refetchOnWindowFocus === "boolean"
        ? options.refetchOnWindowFocus
        : undefined,
  })

  const giantUserAddressQuery = useQuery({
    queryKey: QUERY_KEY_GIANT_USER_ADDRESS,
    queryFn: async ({signal}) => {
      const response = await API.get(
        AppCenterApiName.AppCenterApiWithToken,
        "/user/address",
        {signal},
      )
      const {addresses} = response || {}
      const hasAddress = Object.values(addresses || {}).some(
        addressList => Array.isArray(addressList) && addressList.length > 0,
      )
      if (!hasAddress) {
        return {...response, addresses: {}}
      }
      return response
    },
    enabled: cognitoUser.isSuccess,
    onSuccess: (...args) => {
      options.onSuccess?.(...args)
    },
    onError: (...args) => {
      const data = args[0]?.response?.data
      if (data?.error?.code === ERR_USER_NOT_FOUND) {
        cognitoSignOut()
      }
      options.onError?.(...args)
    },
    ...restOptions,
  })

  return giantUserAddressQuery
}

export const prefetchGiantUserAddress = () => {
  return queryClient.prefetchQuery({
    queryKey: QUERY_KEY_GIANT_USER_ADDRESS,
    queryFn: () => getGiantUserAddress(),
  })
}

/**
 * Hook to get registered products for the current Giant ID account.
 * @param {import("@tanstack/react-query").UseQueryOptions<
 * import("./data").TRegisteredProductsResponse,
 * any,
 * import("./data").TRegisteredProductsResponse,
 * > & {skipErrorNavigate?: boolean}} [options]
 * @returns
 */
export const useGiantRegisteredProducts = (options = {}) => {
  const {skipErrorNavigate, enabled, ...restOptions} = options
  const cognitoUser = useCognitoUser({
    skipErrorNavigate,
    refetchOnMount:
      typeof options.refetchOnMount === "boolean"
        ? options.refetchOnMount
        : undefined,
    refetchOnReconnect:
      typeof options.refetchOnReconnect === "boolean"
        ? options.refetchOnReconnect
        : undefined,
    refetchOnWindowFocus:
      typeof options.refetchOnWindowFocus === "boolean"
        ? options.refetchOnWindowFocus
        : undefined,
  })

  return useQuery({
    retry: 0,
    queryKey: QUERY_KEY_GIANT_REGISTERED_PRODUCTS,
    queryFn: ({signal}) => getGiantRegisteredProducts({signal}),
    enabled:
      cognitoUser.isSuccess && (typeof enabled === "boolean" ? enabled : true),
    ...restOptions,
  })
}

/**
 * Hook to update user address information
 * @param {import("@tanstack/react-query").UseMutationOptions<
 * {message: string},
 * any,
 * {addresses: import("./data").TAddressesRequest},
 * undefined
 * >} [options]
 * @returns
 */
export const useGiantUpdateUserAddress = (options = {}) => {
  const {createConfirmModal} = useConfirmModal()
  const {t} = useI18next()
  const {onSuccess, ...restOptions} = options
  const queryClient = useQueryClient()
  const {mutate, ...restAttrs} = useMutation({
    mutationKey: ["giantUpdateUserAddress"],
    mutationFn: updateGiantUserAddress,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEY_GIANT_USER_ADDRESS,
      })
      // Also invalidate user data since contact address might be linked
      queryClient.invalidateQueries({
        queryKey: QUERY_KEY_GIANT_USER,
      })
      onSuccess?.(data, variables, context)
    },
    onError: () => {
      createConfirmModal(t("common.generalErrorMessage"))
    },
    ...restOptions,
  })

  return {
    updateAddress: mutate,
    ...restAttrs,
  }
}

/**
 * @param {GiantConfirmSignUpParams} param0
 */
export async function giantConfirmSignUp({sub, firstName, lastName}) {
  return API.post(AppCenterApiName.AppCenterApiWithToken, "/user", {
    body: {
      sub,
      firstName,
      lastName,
    },
  })
}

/**
 * @typedef {{
 *  sub: string
 *  firstName: string
 *  lastName: string
 *  registerFrom?: string | null
 * }} GiantConfirmSignUpParams
 * @param {import("@tanstack/react-query").UseMutationOptions<
 * any,
 * any,
 * GiantConfirmSignUpParams,
 * undefined
 * >} [options]
 * @returns
 */
export const useGiantConfirmSignUp = options => {
  const {mutate, ...restAttrs} = useMutation({
    mutationKey: ["giantConfirmSignUp"],
    mutationFn: giantConfirmSignUp,
    ...options,
  })

  return {
    confirm: mutate,
    ...restAttrs,
  }
}

/**
 * @param {import("@tanstack/react-query").UseMutationOptions<
 * any,
 * any,
 * {info?: Partial<import("./data").TGiantUserInfo>, email?: string, phoneNumber?: string, policyVersion?: string},
 * undefined
 * >} [options]
 * @returns
 */
export const useGiantUpdateUser = (options = {}) => {
  const {createConfirmModal} = useConfirmModal()
  const {t} = useI18next()
  const {onSuccess, ...restOptions} = options
  const queryClient = useQueryClient()
  const {mutate, ...restAttrs} = useMutation({
    mutationKey: ["giantUpdateUser"],
    mutationFn: info =>
      API.put(AppCenterApiName.AppCenterApiWithToken, "/user", {
        body: info,
      }),
    onSuccess: (...args) => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEY_GIANT_USER,
      })
      onSuccess?.(...args)
    },
    onError: () => {
      createConfirmModal(t("common.generalErrorMessage"))
    },
    ...restOptions,
  })

  return {
    update: mutate,
    ...restAttrs,
  }
}

/**
 * @param {import("@tanstack/react-query").UseMutationOptions<
 * any,
 * any,
 * {email: string, code: string},
 * undefined
 * >} [options]
 * @returns
 */
export const useGiantUpdateEmail = (options = {}) => {
  const {onSuccess, ...restOptions} = options
  const queryClient = useQueryClient()
  const {mutate, ...restAttrs} = useMutation({
    mutationKey: ["giantUpdatePhoneEmail"],
    mutationFn: async ({email, code}) => {
      const user = await Auth.currentAuthenticatedUser()
      await Auth.verifyUserAttributeSubmit(user, "email", code)
      await Auth.currentAuthenticatedUser({bypassCache: true})
      await API.put(AppCenterApiName.AppCenterApiWithToken, "/user", {
        body: {
          email,
          info: {},
        },
      })
    },
    onSuccess: (...args) => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEY_GIANT_USER,
      })
      onSuccess?.(...args)
    },
    ...restOptions,
  })

  return {
    update: mutate,
    ...restAttrs,
  }
}

/**
 * @param {import("@tanstack/react-query").UseMutationOptions<
 * any,
 * any,
 * {phoneNumber: string, code: string},
 * undefined
 * >} [options]
 * @returns
 */
export const useGiantUpdatePhoneNumber = (options = {}) => {
  const {onSuccess, ...restOptions} = options
  const queryClient = useQueryClient()
  const {mutate, ...restAttrs} = useMutation({
    mutationKey: ["giantUpdatePhoneNumber"],
    mutationFn: async ({phoneNumber, code}) => {
      const user = await Auth.currentAuthenticatedUser()
      await Auth.verifyUserAttributeSubmit(user, "phone_number", code)
      await Auth.currentAuthenticatedUser({bypassCache: true})
      await API.put(AppCenterApiName.AppCenterApiWithToken, "/user", {
        body: {
          phoneNumber,
          info: {},
        },
      })
    },
    onSuccess: (...args) => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEY_GIANT_USER,
      })
      onSuccess?.(...args)
    },
    ...restOptions,
  })

  return {
    update: mutate,
    ...restAttrs,
  }
}

/**
 * @param {import("@tanstack/react-query").UseMutationOptions<
 * any,
 * any,
 * boolean,
 * undefined
 * >} [options]
 * @returns
 */
export const useGiantUpdateMarketingConsentStatus = options => {
  return useMutation({
    mutationKey: ["giantUpdateMarketingConsentStatus"],
    mutationFn: checked =>
      API.put(AppCenterApiName.AppCenterApiWithToken, "/user", {
        body: {
          info: {
            marketingConsent: checked,
          },
        },
      }),
    ...options,
  })
}

/**
 * @param {import("@tanstack/react-query").UseMutationOptions<
 * any,
 * any,
 * boolean,
 * undefined
 * >} [options]
 * @returns
 */
export const useGiantUpdateMFAStatus = options => {
  return useMutation({
    mutationKey: ["giantUpdateMFAStatus"],
    mutationFn: enabledStatus =>
      API.put(AppCenterApiName.AppCenterApiWithToken, "/account", {
        body: {
          mfaEnabled: enabledStatus,
        },
      }),
    ...options,
  })
}

/**
 * @param {import("@tanstack/react-query").UseMutationOptions<
 * any,
 * any,
 * void,
 * undefined
 * >} [options]
 * @returns
 */
export function useDeleteGiantAccount(options = {}) {
  return useMutation({
    mutationKey: ["deleteGiantAccount"],
    mutationFn: async () => {
      await API.del(AppCenterApiName.AppCenterApiWithToken, "/user", {})
      await cognitoSignOut({global: true})
    },
    ...options,
  })
}

/**
 * @param {import("@tanstack/react-query").UseQueryOptions<
 * import('./data').TFamilyMembers,
 * any,
 * import('./data').TFamilyMembers,
 * >} [options]
 */
export function useGiantFamilyMembers(options = {}) {
  const cognitoUser = useCognitoUser()
  const {enabled, ...restOptions} = options

  return useQuery({
    retry: 0,
    queryKey: QUERY_KEY_GIANT_FAMILY_MEMBERS,
    queryFn: () =>
      API.get(AppCenterApiName.AppCenterApiWithToken, "/family", {})
        .then(data => FamilyMembersResponseDTO.parse(data.data).members)
        .catch(err => {
          if (Axios.isAxiosError(err)) {
            // for 400, it means no family, let's just return empty array
            if (err.response?.status === 400) {
              return []
            }
          }
          throw err
        }),
    enabled:
      cognitoUser.isSuccess && (typeof enabled === "boolean" ? enabled : true),
    ...restOptions,
  })
}

/**
 * @param {import("@tanstack/react-query").UseMutationOptions<
 * any,
 * any,
 * void,
 * undefined
 * >} [options]
 * @returns
 */
export function useDeleteGiantFamily(options = {}) {
  const {onSuccess, ...restOptions} = options
  const queryClient = useQueryClient()
  return useMutation({
    mutationKey: ["deleteGiantFamily"],
    mutationFn: async () => {
      return API.del(AppCenterApiName.AppCenterApiWithToken, "/family", {})
    },
    onSuccess: (...args) => {
      // reset the user store family data
      useGiantUserStore.setState({
        family: {
          editor: undefined,
          id: undefined,
          invitee: undefined,
          membership: undefined,
        },
      })
      queryClient.invalidateQueries({
        queryKey: QUERY_KEY_GIANT_USER,
      })
      queryClient.invalidateQueries({
        queryKey: QUERY_KEY_GIANT_FAMILY_MEMBERS,
      })
      onSuccess?.(...args)
    },
    ...restOptions,
  })
}

/**
 * @param {import("@tanstack/react-query").UseMutationOptions<
 * any,
 * any,
 * string,
 * undefined
 * >} [options]
 * @returns
 */
export function useTransferFamilyAdmin(options = {}) {
  const {onSuccess, ...restOptions} = options
  const queryClient = useQueryClient()
  return useMutation({
    mutationKey: ["transferGiantFamilyAdmin"],
    mutationFn:
      /**
       * @param {string} gid
       */
      async gid => {
        return API.post(
          AppCenterApiName.AppCenterApiWithToken,
          "/transfer-member-permission",
          {
            body: {
              gid,
            },
          },
        )
      },
    onSuccess: (...args) => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEY_GIANT_USER,
      })
      queryClient.invalidateQueries({
        queryKey: QUERY_KEY_GIANT_FAMILY_MEMBERS,
      })
      onSuccess?.(...args)
    },
    ...restOptions,
  })
}

/**
 * @param {import("@tanstack/react-query").UseMutationOptions} [options]
 * @returns
 */
export function useCreateGiantFamily(options = {}) {
  const {onSuccess, ...restOptions} = options
  const queryClient = useQueryClient()
  return useMutation({
    mutationKey: ["createFamily"],
    mutationFn: () => {
      return API.post(AppCenterApiName.AppCenterApiWithToken, "/family", {
        body: {},
      })
    },
    onSuccess: (...args) => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEY_GIANT_USER,
      })
      queryClient.invalidateQueries({
        queryKey: QUERY_KEY_GIANT_FAMILY_MEMBERS,
      })
      onSuccess?.(...args)
    },
    ...restOptions,
  })
}

/**
 * @param {import("@tanstack/react-query").UseMutationOptions<
 *  any,
 *  any,
 *  string,
 *  undefined
 * >} [options]
 * @returns
 */
export function useJoinGiantFamily(options = {}) {
  const {onSuccess, ...restOptions} = options
  const queryClient = useQueryClient()
  return useMutation({
    mutationKey: ["joinFamily"],
    mutationFn: familyId => {
      return API.post(
        AppCenterApiName.AppCenterApiWithToken,
        "/add-to-family",
        {
          body: {
            familyId,
          },
        },
      )
    },
    onSuccess: (...args) => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEY_GIANT_USER,
      })
      queryClient.invalidateQueries({
        queryKey: QUERY_KEY_GIANT_FAMILY_MEMBERS,
      })
      onSuccess?.(...args)
    },
    ...restOptions,
  })
}

/**
 * @param {import("@tanstack/react-query").UseMutationOptions} [options]
 * @returns
 */
export function useLeaveGiantFamily(options = {}) {
  const {onSuccess, ...restOptions} = options
  const queryClient = useQueryClient()
  return useMutation({
    mutationKey: ["leaveFamily"],
    mutationFn: () => {
      return API.del(AppCenterApiName.AppCenterApiWithToken, "/leave-family", {
        body: {},
      })
    },
    onSuccess: (...args) => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEY_GIANT_USER,
      })
      queryClient.invalidateQueries({
        queryKey: QUERY_KEY_GIANT_FAMILY_MEMBERS,
      })
      onSuccess?.(...args)
    },
    ...restOptions,
  })
}

/**
 * @param {import("@tanstack/react-query").UseMutationOptions<
 *  any,
 * any,
 * {email: string, gid?: string},
 * undefined
 * >} [options]
 * @returns
 */
export function useInviteFamilyMemberViaEmail(options = {}) {
  return useMutation({
    mutationKey: ["inviteFamilyMemberViaEmail"],
    mutationFn: ({email, gid}) => {
      return API.post(
        AppCenterApiName.AppCenterApiWithToken,
        "/send-family-invitation",
        {
          body: {email, gid},
        },
      )
    },
    ...options,
  })
}

/**
 * @param {import("@tanstack/react-query").UseQueryOptions<
 * import("./data").TFamilyMemberProfile,
 * any,
 * import("./data").TFamilyMemberProfile,
 * > & {gid?: string}} [options]
 */
export function useGiantFamilyMemberProfile(options = {}) {
  const {gid, ...restOptions} = options
  return useQuery({
    retry: 0,
    queryKey: giantFamilyMemberProfileKey(gid),
    queryFn: () =>
      API.post(
        AppCenterApiName.AppCenterApiWithToken,
        "/search-member-profile",
        {
          body: {
            gid,
          },
        },
      ).then(data => FamilyMemberProfile.parse(data.data)),
    ...restOptions,
  })
}

/**
 * @param {import("@tanstack/react-query").UseMutationOptions<
 *  any,
 * any,
 * {gid: string, isTempMember: boolean},
 * undefined
 * >} [options]
 * @returns
 */
export function useRemoveGiantFamilyMember(options = {}) {
  const {onSuccess, ...restOptions} = options
  const queryClient = useQueryClient()
  return useMutation({
    mutationKey: ["removeGiantFamilyMember"],
    mutationFn: ({gid, isTempMember}) => {
      if (isTempMember)
        return API.del(
          AppCenterApiName.AppCenterApiWithToken,
          "/delete-member",
          {
            body: {gid},
          },
        )
      return API.del(AppCenterApiName.AppCenterApiWithToken, "/remove-member", {
        body: {gid},
      })
    },
    onSuccess: (...args) => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEY_GIANT_FAMILY_MEMBERS,
      })
      onSuccess?.(...args)
    },
    ...restOptions,
  })
}

/**
 * @param {import("@tanstack/react-query").UseMutationOptions<
 *  any,
 * any,
 * {editor: boolean, gid: string},
 * undefined
 * >} [options]
 * @returns
 */
export function useUpdateGiantFamilyMemberPermission(options = {}) {
  const {onSuccess, ...restOptions} = options
  const queryClient = useQueryClient()
  return useMutation({
    mutationKey: ["updateGiantFamilyMemberPermission"],
    mutationFn: ({editor, gid}) => {
      return API.post(
        AppCenterApiName.AppCenterApiWithToken,
        "/update-member-permission",
        {
          body: {editor, gid},
        },
      )
    },
    onSuccess: (...args) => {
      queryClient.invalidateQueries({
        queryKey: giantFamilyMemberProfileKey(args[1].gid),
      })
      onSuccess?.(...args)
    },
    ...restOptions,
  })
}

/**
 * @param {import("@tanstack/react-query").UseMutationOptions<
 *  any,
 * any,
 * {firstName: string, lastName: string},
 * undefined
 * >} [options]
 * @returns
 */
export function useCreateGiantFamilyMember(options = {}) {
  const {onSuccess, ...restOptions} = options
  const queryClient = useQueryClient()
  return useMutation({
    mutationKey: ["createGiantFamilyMember"],
    mutationFn: ({firstName, lastName}) => {
      return API.post(
        AppCenterApiName.AppCenterApiWithToken,
        "/create-member",
        {
          body: {firstName, lastName},
        },
      )
    },
    onSuccess: (...args) => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEY_GIANT_FAMILY_MEMBERS,
      })
      onSuccess?.(...args)
    },
    ...restOptions,
  })
}

/**
 * @param {import("@tanstack/react-query").UseMutationOptions<
 *  any,
 * any,
 * import("services/auth/types").TAvailableSocialProvider,
 * undefined
 * >} [options]
 * @returns
 */
export function useCancelLinkThirdPartyAccount(options = {}) {
  const {onSuccess, ...restOptions} = options
  const queryClient = useQueryClient()
  return useMutation({
    mutationKey: ["cancelLinkThirdPartyAccount"],
    mutationFn: async providerName => {
      const clientId = process.env.GATSBY_AWS_USER_POOL_WEB_CLIENT_ID
      const redirectUri = `${window.location.origin}/auth/unlink`

      // Open the Cognito Logout endpoint in a new tab
      window.open(
        `https://${process.env.GATSBY_AWS_OATH_DOMAIN}/logout?client_id=${clientId}&logout_uri=${redirectUri}`,
        "_blank",
      )
      registerCloseWindow()

      return API.del(AppCenterApiName.AppCenterApiWithToken, "/account", {
        body: {
          providerName:
            providerName === AvailableSocialProvider.enum.Apple
              ? AvailableCognitoIdentity.enum.SignInWithApple
              : providerName,
        },
      })
    },
    onSuccess: (...args) => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEY_GIANT_USER,
      })
      onSuccess?.(...args)
    },
    ...restOptions,
  })
}

/**
 * @param {import("@tanstack/react-query").UseMutationOptions<
 *  any,
 * any,
 * {gid: string, sub: string},
 * undefined
 * >} [options]
 * @returns
 */
export function useGiantLinkTempMemberAccount(options = {}) {
  const {onSuccess, ...restOptions} = options
  const queryClient = useQueryClient()
  return useMutation({
    mutationKey: ["giantLinkTempMemberAccount"],
    mutationFn: ({gid, sub}) => {
      return API.post(
        AppCenterApiName.AppCenterApiWithToken,
        "/link-member-account",
        {
          body: {gid, sub},
        },
      )
    },
    onSuccess: (...args) => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEY_GIANT_FAMILY_MEMBERS,
      })
      queryClient.invalidateQueries({
        queryKey: giantFamilyMemberProfileKey(args[1].gid),
      })
      onSuccess?.(...args)
    },
    ...restOptions,
  })
}

/**
 * @param {import("@tanstack/react-query").UseMutationOptions<
 *  any,
 * any,
 * {gid: string, info: import("./data").TFamilyMemberProfile['info']},
 * undefined
 * >} [options]
 * @returns
 */
export function useGiantUpdateTempMemberProfile(options = {}) {
  const {onSuccess, ...restOptions} = options
  const queryClient = useQueryClient()
  return useMutation({
    mutationKey: ["giantUpdateTempMemberProfile"],
    mutationFn: ({gid, info}) => {
      return API.post(
        AppCenterApiName.AppCenterApiWithToken,
        "/update-member-profile",
        {
          body: {gid, info},
        },
      )
    },
    onSuccess: (...args) => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEY_GIANT_FAMILY_MEMBERS,
      })
      queryClient.invalidateQueries({
        queryKey: giantFamilyMemberProfileKey(args[1].gid),
      })
      onSuccess?.(...args)
    },
    ...restOptions,
  })
}

/**
 * @typedef {{
 *  id: string,
 *  admin: {
 *    firstName: string,
 *    lastName: string
 * }}} TGiantSearchFamilyResult
 * @param {string} familyId
 * @param {import("@tanstack/react-query").UseQueryOptions<
 * any,
 * any,
 * TGiantSearchFamilyResult,
 * >} [options]
 * @returns
 */
export const useGiantSearchFamily = (familyId, options) => {
  return useQuery({
    queryKey: ["giantSearchFamily"],
    queryFn: () =>
      API.post(AppCenterApiName.AppCenterApiWithToken, "/search-family", {
        body: {familyId},
      }),
    enabled: !!familyId,
    ...options,
  })
}

/**
 * @param {import("@tanstack/react-query").UseMutationOptions<
 *  any,
 * any,
 * string,
 * undefined
 * >} [options]
 * @returns
 */
export function useGiantManualLinkAccount(options = {}) {
  const {onSuccess, ...restOptions} = options
  const queryClient = useQueryClient()
  return useMutation({
    mutationKey: ["giantManualLinkAccount"],
    mutationFn: gid => {
      return API.post(
        AppCenterApiName.AppCenterApiWithToken,
        "/manual-link-account",
        {
          body: {gid},
        },
      )
    },
    onSuccess: (...args) => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEY_GIANT_USER,
      })
      queryClient.invalidateQueries({
        queryKey: QUERY_KEY_GIANT_FAMILY_MEMBERS,
      })
      queryClient.invalidateQueries({
        queryKey: giantFamilyMemberProfileKey(args[1]),
      })
      onSuccess?.(...args)
    },
    ...restOptions,
  })
}

/**
 * @param {import("@tanstack/react-query").UseMutationOptions<
 *  any,
 * any,
 * {oldPassword: string, newPassword: string},
 * undefined
 * >} [options]
 * @returns
 */
export function useGiantChangePassword(options = {}) {
  return useMutation({
    mutationKey: ["giantChangePassword"],
    mutationFn: ({oldPassword, newPassword}) =>
      API.put(AppCenterApiName.AppCenterApiWithToken, "/account", {
        body: {
          password: {
            oldPassword,
            newPassword,
          },
        },
      }),
    ...options,
  })
}

/**
 * @typedef {{
 *   name: string
 *   callbackUrl: string
 *  }} TGiantClientCallbackUrlResult
 * @param {string} clientId
 * @param {import("@tanstack/react-query").UseQueryOptions<
 * any,
 * any,
 * TGiantClientCallbackUrlResult,
 * >} [options]
 * @returns
 */
export function useSearchCallbackUrl(clientId, options = {}) {
  return useQuery({
    queryKey: ["giantSearchCallbackUrl"],
    queryFn: () => searchCallbackUrl(clientId),
    ...options,
  })
}

/**
 * @typedef {import('services/auth/hooks').CognitoUser} CognitoUser
 */

const SALESFORCE_API_URL = `${process.env.SALESFORCE_API_URL}/services/apexrest/gid/case`

/**
 * @typedef {{name: string, email: string, description: string, type: string, service: string, language: string, country?: string}} TCaseData
 * @param {import('@tanstack/react-query').UseMutationOptions<any, any, TCaseData, undefined>} [options]
 * @returns
 */

export function useCreateCase(options = {}) {
  return useMutation({
    mutationKey: ["createCaseV2"],
    mutationFn: async data => {
      const {token} = await API.get(
        AppCenterApiName.AppCenterApi,
        "/salesforce/token",
        {},
      )
      return await Axios.post(SALESFORCE_API_URL, data, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
    },
    ...options,
  })
}

const CONTACT_TICKET_MAX_RETRIES = 3
const CONTACT_TICKET_TIMEOUT_MS = 10000

/**
 * @param {unknown} error
 * @returns {number | undefined}
 */
const getResponseStatus = error =>
  /** @type {{response?: {status?: number}} | null | undefined} */ (error)
    ?.response?.status

/**
 * A timeout or network error has no response, so it is retried like a 5xx.
 * @param {unknown} error
 */
const isRetryableContactTicketError = error => {
  const status = getResponseStatus(error)
  return status === undefined || status >= 500
}

/**
 * Sends the contact-us ticket to GID in the background. The user never sees
 * the outcome, so the final failure is only logged, without PII.
 * When testing, use an @example.com or @example.nl email. Any other address
 * gets a real support reply. See https://github.com/BCT-TPE/Giant-ID/issues/48
 */
export function useCreateContactTicket() {
  return useMutation({
    mutationKey: ["createContactTicket"],
    mutationFn: (
      /** @type {import("features/contact-ticket").ContactTicket} */ ticket,
    ) =>
      API.post(AppCenterApiName.AppCenterApi, "/contact-us/ticket", {
        body: ticket,
        headers: {"x-api-key": process.env.GATSBY_CONTACT_US_API_KEY ?? ""},
        timeout: CONTACT_TICKET_TIMEOUT_MS,
      }),
    retry: (failureCount, error) =>
      failureCount < CONTACT_TICKET_MAX_RETRIES &&
      isRetryableContactTicketError(error),
    onError: (error, ticket) => {
      console.error("Failed to send contact ticket", {
        status: getResponseStatus(error),
        requestId: ticket.requestId,
      })
    },
  })
}

/**
 *
 * @param {import("@tanstack/react-query").UseQueryOptions<
 *  import("services/appCenter/data").PhoneCodeOptions,
 *  unknown,
 *  import("services/appCenter/data").PhoneCodeOptions
 * >} [options]
 * @returns
 */
export function usePhoneCodeOptions(options = {}) {
  return useQuery({
    queryKey: ["phoneCodeOptions"],
    queryFn: async () => {
      const data = await API.get(
        AppCenterApiName.AppCenterApi,
        "/allowed-countries",
        {},
      )
      return PhoneCodeOptions.parse(data)
    },
    retry: 2,
    staleTime: 10 * 60 * 1000,
    ...options,
  })
}
